import { useState } from 'react';
import { 
  Shield, CheckCircle, AlertTriangle, ExternalLink, Copy, 
  Key, Lock, Users, Settings, FileText, Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

export default function M365SetupGuidePage() {
  const [copiedStep, setCopiedStep] = useState(null);
  
  // Dynamic redirect URI based on current domain
  const getRedirectUri = () => {
    const hostname = window.location.hostname;
    if (hostname === 'compliance.cyberforte.com.au') {
      return 'https://compliance.cyberforte.com.au/api/auth/m365/callback';
    }
    return `${window.location.origin}/api/auth/m365/callback`;
  };
  
  const redirectUri = getRedirectUri();

  const copyToClipboard = (text, step) => {
    navigator.clipboard.writeText(text);
    setCopiedStep(step);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedStep(null), 2000);
  };

  const steps = [
    {
      number: 1,
      title: "Create Azure AD App Registration",
      icon: Settings,
      substeps: [
        "Go to Azure Portal → Azure Active Directory → App registrations",
        "Click '+ New registration'",
        "Name: 'Cyber Forte Compliance Manager'",
        "Supported account types: 'Single tenant'",
        `Redirect URI: Web - ${redirectUri}`,
        "Click 'Register'"
      ],
      action: {
        label: "Open Azure Portal",
        url: "https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade"
      }
    },
    {
      number: 2,
      title: "Copy Application IDs",
      icon: Key,
      substeps: [
        "After registration, you'll see the Overview page",
        "Copy 'Application (client) ID'",
        "Copy 'Directory (tenant) ID'",
        "Save these values securely"
      ]
    },
    {
      number: 3,
      title: "Create Client Secret",
      icon: Lock,
      substeps: [
        "Click 'Certificates & secrets' in left menu",
        "Click '+ New client secret'",
        "Description: 'Cyber Forte Integration'",
        "Expires: 24 months (recommended)",
        "Click 'Add'",
        "⚠️ COPY THE VALUE IMMEDIATELY - shown only once!"
      ]
    },
    {
      number: 4,
      title: "Configure API Permissions",
      icon: Shield,
      substeps: [
        "Click 'API permissions' in left menu",
        "Click '+ Add a permission' → Microsoft Graph → Application permissions",
        "Add these permissions:",
        "  • SecurityEvents.Read.All",
        "  • SecurityActions.Read.All",  
        "  • SecurityAlert.Read.All",
        "  • DeviceManagementManagedDevices.Read.All",
        "  • User.Read.All",
        "  • Directory.Read.All",
        "  • Policy.Read.All",
        "  • Organization.Read.All",
        "⚠️ Click 'Grant admin consent' button",
        "⚠️ Confirm - all permissions must show green checkmarks"
      ]
    },
    {
      number: 5,
      title: "Update Backend Configuration",
      icon: FileText,
      substeps: [
        "Update /app/backend/.env file:",
        'M365_DEMO_MODE="false"',
        'M365_CLIENT_ID="your-client-id"',
        'M365_CLIENT_SECRET="your-secret"',
        'M365_TENANT_ID="your-tenant-id"',
        "Restart backend: sudo supervisorctl restart backend"
      ]
    },
    {
      number: 6,
      title: "Connect & Test",
      icon: Users,
      substeps: [
        "Go to M365 Integration page",
        "Click 'Connect Microsoft 365'",
        "Sign in with Global Admin account",
        "Accept permissions",
        "Verify Microsoft Secure Score appears"
      ]
    }
  ];

  const requiredPermissions = [
    { name: "SecurityEvents.Read.All", description: "Read security events and Secure Score" },
    { name: "SecurityActions.Read.All", description: "Read security actions and controls" },
    { name: "SecurityAlert.Read.All", description: "Read security alerts" },
    { name: "DeviceManagementManagedDevices.Read.All", description: "Read Intune managed devices" },
    { name: "User.Read.All", description: "Read user profiles" },
    { name: "Directory.Read.All", description: "Read directory data" },
    { name: "Policy.Read.All", description: "Read organization policies" },
    { name: "Organization.Read.All", description: "Read organization information" }
  ];

  return (
    <div className="max-w-5xl mx-auto space-y-8 pb-12">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white rounded-sm p-8 -mx-6">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="w-10 h-10" />
          <h1 className="text-3xl font-heading font-bold">M365 Live Integration Setup</h1>
        </div>
        <p className="text-xl text-slate-100">
          Connect your Microsoft 365 tenant to fetch real security data
        </p>
      </div>

      {/* Prerequisites */}
      <div className="bg-slate-50 border border-slate-700 rounded-sm p-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-slate-600 flex-shrink-0 mt-1" />
          <div>
            <h2 className="text-lg font-heading font-semibold text-slate-900 mb-2">
              Prerequisites
            </h2>
            <ul className="space-y-1 text-sm text-slate-900">
              <li>• Microsoft 365 Business or Enterprise subscription</li>
              <li>• Global Administrator or Application Administrator role</li>
              <li>• Access to Azure Portal (https://portal.azure.com)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Quick Copy Section */}
      <div className="bg-slate-50 border border-slate-700 rounded-sm p-6">
        <h3 className="font-semibold text-slate-900 mb-3">Quick Copy: Redirect URI</h3>
        <div className="flex items-center gap-2">
          <code className="flex-1 bg-white px-4 py-2 rounded text-sm border border-slate-700">
            {redirectUri}
          </code>
          <Button
            onClick={() => copyToClipboard(redirectUri, 'redirect')}
            variant="outline"
            className="rounded-sm"
          >
            {copiedStep === 'redirect' ? (
              <Check className="w-4 h-4 text-green-600" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Setup Steps */}
      <div className="space-y-6">
        <h2 className="text-2xl font-heading font-semibold text-slate-900">
          Setup Steps
        </h2>
        
        {steps.map((step) => {
          const Icon = step.icon;
          return (
            <div key={step.number} className="bg-white border border-border rounded-sm overflow-hidden">
              <div className="bg-slate-50 border-b border-border p-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-slate-600 text-white rounded-full flex items-center justify-center font-bold">
                    {step.number}
                  </div>
                  <Icon className="w-5 h-5 text-slate-600" />
                  <h3 className="text-lg font-heading font-semibold text-slate-900">
                    {step.title}
                  </h3>
                </div>
              </div>
              
              <div className="p-6">
                <ol className="space-y-2">
                  {step.substeps.map((substep, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-slate-700">{substep}</span>
                    </li>
                  ))}
                </ol>
                
                {step.action && (
                  <div className="mt-4">
                    <Button
                      onClick={() => window.open(step.action.url, '_blank')}
                      className="bg-slate-600 text-white hover:bg-slate-700 rounded-sm"
                    >
                      {step.action.label}
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Required Permissions Table */}
      <div className="bg-white border border-border rounded-sm p-6">
        <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4">
          Required API Permissions (Application Type)
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 px-3 font-semibold text-slate-900">Permission</th>
                <th className="text-left py-2 px-3 font-semibold text-slate-900">Description</th>
              </tr>
            </thead>
            <tbody>
              {requiredPermissions.map((perm, idx) => (
                <tr key={idx} className="border-b border-border last:border-0">
                  <td className="py-2 px-3">
                    <code className="bg-slate-100 px-2 py-1 rounded text-xs">
                      {perm.name}
                    </code>
                  </td>
                  <td className="py-2 px-3 text-slate-600">{perm.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Troubleshooting */}
      <div className="bg-slate-50 border border-slate-700 rounded-sm p-6">
        <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4">
          Common Issues & Solutions
        </h3>
        <div className="space-y-3 text-sm">
          <div>
            <p className="font-medium text-slate-900">❌ "Invalid Client Secret"</p>
            <p className="text-slate-600 ml-5">
              → Secret expired or incorrect. Create new secret and update backend .env
            </p>
          </div>
          <div>
            <p className="font-medium text-slate-900">❌ "Insufficient Permissions"</p>
            <p className="text-slate-600 ml-5">
              → Missing admin consent. Grant admin consent for all permissions (green checkmarks required)
            </p>
          </div>
          <div>
            <p className="font-medium text-slate-900">❌ "Redirect URI Mismatch"</p>
            <p className="text-slate-600 ml-5">
              → Verify redirect URI matches exactly in both Azure AD and backend .env
            </p>
          </div>
          <div>
            <p className="font-medium text-slate-900">❌ "401 Unauthorized"</p>
            <p className="text-slate-600 ml-5">
              → Token expired. Disconnect and reconnect the integration
            </p>
          </div>
        </div>
      </div>

      {/* Security Notice */}
      <div className="bg-slate-50 border border-slate-700 rounded-sm p-6">
        <div className="flex items-start gap-3">
          <Lock className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
          <div>
            <h3 className="font-semibold text-green-900 mb-2">Security & Privacy</h3>
            <ul className="space-y-1 text-sm text-green-900">
              <li>✅ All permissions are read-only - app cannot modify your M365 tenant</li>
              <li>✅ Access tokens stored encrypted in your database</li>
              <li>✅ No access to email content, files, or personal messages</li>
              <li>✅ All API calls logged for audit purposes</li>
              <li>✅ Compliant with Microsoft security best practices</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Documentation Link */}
      <div className="text-center">
        <p className="text-sm text-slate-600 mb-3">
          For detailed documentation, see:
        </p>
        <code className="bg-slate-100 px-4 py-2 rounded text-sm">
          /app/M365_LIVE_SETUP_GUIDE.md
        </code>
      </div>
    </div>
  );
}
